"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppointmentApiErrorRegistry = void 0;
/**
 * Per-endpoint error registry for the Appointment bounded context —
 * which `AppointmentErrorCodes` (plus the generic
 * `APPOINTMENT_VALIDATION_ERROR` every endpoint can return) a specific
 * endpoint can actually respond with. Same shape and purpose as
 * `patient-error-registry.ts`; this is what
 * `appointment.operations.ts`'s OpenAPI `errorCodes` field is built
 * from, and what `docs/APPOINTMENT-API-ERROR-GUIDE.md` documents per
 * endpoint.
 */
const appointment_error_codes_1 = require("./appointment-error-codes");
const appointment_error_codes_2 = require("./appointment-error-codes");
const GENERIC_CODES = [appointment_error_codes_1.AppointmentErrorCodes.VALIDATION_ERROR];
function toContract(code) {
    return { code, ...(0, appointment_error_codes_2.getAppointmentErrorCatalogEntry)(code) };
}
function endpoint(name, codes) {
    return { endpoint: name, errors: [...codes, ...GENERIC_CODES].map(toContract) };
}
exports.AppointmentApiErrorRegistry = {
    // Booking
    createAppointment: endpoint('createAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    walkInAppointment: endpoint('walkInAppointment', [appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED]),
    emergencyAppointment: endpoint('emergencyAppointment', [appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED]),
    corporateAppointment: endpoint('corporateAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    referralAppointment: endpoint('referralAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    telemedicineAppointment: endpoint('telemedicineAppointment', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT,
        appointment_error_codes_1.AppointmentErrorCodes.TELEMEDICINE_SESSION_UNAVAILABLE,
    ]),
    healthCheckAppointment: endpoint('healthCheckAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    packageAppointment: endpoint('packageAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    // Scheduling
    rescheduleAppointment: endpoint('rescheduleAppointment', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT,
    ]),
    cancelAppointment: endpoint('cancelAppointment', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND]),
    confirmAppointment: endpoint('confirmAppointment', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_INVALID_STATUS_TRANSITION,
    ]),
    appointmentExpiry: endpoint('appointmentExpiry', []),
    // Check-In
    patientArrival: endpoint('patientArrival', [appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED]),
    patientCheckIn: endpoint('patientCheckIn', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_INVALID_STATUS_TRANSITION,
        appointment_error_codes_1.AppointmentErrorCodes.CHECK_IN_VISIT_TYPE_REQUIRED,
        appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED,
    ]),
    lateArrival: endpoint('lateArrival', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_INVALID_STATUS_TRANSITION,
        appointment_error_codes_1.AppointmentErrorCodes.CHECK_IN_VISIT_TYPE_REQUIRED,
        appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED,
    ]),
    noShow: endpoint('noShow', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_INVALID_STATUS_TRANSITION,
    ]),
    completeAppointment: endpoint('completeAppointment', [
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_INVALID_STATUS_TRANSITION,
    ]),
    // Queue
    /*
      P0-3 — the queue read. No endpoint-specific codes: a filtered, paginated
      read whose only failures are the generic validation/authorisation ones.
      An empty waiting room is a result, not an error.
    */
    listQueue: endpoint('listQueue', []),
    generateQueue: endpoint('generateQueue', [appointment_error_codes_1.AppointmentErrorCodes.QUEUE_TOKEN_NOT_FOUND]),
    generateToken: endpoint('generateToken', [appointment_error_codes_1.AppointmentErrorCodes.SEQUENCE_NOT_CONFIGURED]),
    moveQueue: endpoint('moveQueue', [
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_INVALID_STATUS_TRANSITION,
    ]),
    recallPatient: endpoint('recallPatient', [
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_INVALID_STATUS_TRANSITION,
    ]),
    skipPatient: endpoint('skipPatient', [
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.QUEUE_ENTRY_INVALID_STATUS_TRANSITION,
    ]),
    priorityQueue: endpoint('priorityQueue', []),
    // Waitlist
    joinWaitlist: endpoint('joinWaitlist', []),
    promoteWaitlist: endpoint('promoteWaitlist', [
        appointment_error_codes_1.AppointmentErrorCodes.WAITLIST_DOCTOR_REQUIRED,
        appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT,
    ]),
    expireWaitlist: endpoint('expireWaitlist', []),
    // Slot Management
    getAvailableSlots: endpoint('getAvailableSlots', []),
    holdSlot: endpoint('holdSlot', [appointment_error_codes_1.AppointmentErrorCodes.APPOINTMENT_SLOT_CONFLICT]),
    releaseSlot: endpoint('releaseSlot', [
        appointment_error_codes_1.AppointmentErrorCodes.SLOT_HOLD_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.SLOT_HOLD_TOKEN_MISMATCH,
    ]),
    // Doctor Availability
    createSchedule: endpoint('createSchedule', []),
    updateSchedule: endpoint('updateSchedule', [appointment_error_codes_1.AppointmentErrorCodes.DOCTOR_SCHEDULE_NOT_FOUND]),
    doctorLeave: endpoint('doctorLeave', []),
    availabilityOverride: endpoint('availabilityOverride', []),
    // Reminder
    scheduleReminder: endpoint('scheduleReminder', []),
    cancelReminder: endpoint('cancelReminder', [
        appointment_error_codes_1.AppointmentErrorCodes.REMINDER_NOT_FOUND,
        appointment_error_codes_1.AppointmentErrorCodes.REMINDER_INVALID_STATUS_TRANSITION,
    ]),
};
//# sourceMappingURL=appointment-error-registry.js.map